/**
 * usePresentationEstimate — debounced estimator для цены презентации.
 *
 * Бэк пересчитывает цену в зависимости от режима (edit/create), содержимого
 * PPTX (для edit — скачивает и считает токены по тексту слайдов), числа
 * слайдов (create) и количества прикреплённых картинок. Юзер видит цену
 * до нажатия «Создать».
 *
 * Debounce 600ms — совпадает со старой страницей BotsSlides, даёт юзеру
 * спокойно печатать промпт не отправляя по запросу на каждый символ.
 *
 * AbortController отменяет предыдущий in-flight запрос при новых изменениях.
 */
import { useEffect, useRef, useState } from 'react'
import {
	estimatePresentation,
	type PresentationEstimatePayload,
} from '../../../api/presentations'

interface EstimateInputs {
	mode: 'edit' | 'create'
	prompt: string
	fileUrl?: string
	slideCount?: number
	imageCount: number
	/** `false` пропускает оценку (например пока пользователь не выбрал модель) */
	enabled: boolean
}

interface EstimateState {
	creditsPrice: number | null
	slidesCount: number | null
	estimating: boolean
}

const DEBOUNCE_MS = 600
const MIN_PROMPT_LEN = 3

export function usePresentationEstimate(inputs: EstimateInputs): EstimateState {
	const [creditsPrice, setCreditsPrice] = useState<number | null>(null)
	const [slidesCount, setSlidesCount] = useState<number | null>(null)
	const [estimating, setEstimating] = useState(false)

	const abortRef = useRef<AbortController | null>(null)

	useEffect(() => {
		if (!inputs.enabled) {
			setCreditsPrice(null)
			setSlidesCount(null)
			return
		}

		const trimmed = inputs.prompt.trim()
		const hasEditInputs = inputs.mode === 'edit' && !!inputs.fileUrl && trimmed.length >= MIN_PROMPT_LEN
		const hasCreateInputs = inputs.mode === 'create' && trimmed.length >= MIN_PROMPT_LEN

		if (!hasEditInputs && !hasCreateInputs) {
			setCreditsPrice(null)
			setSlidesCount(null)
			return
		}

		// Перед новым запросом отменяем предыдущий (и in-flight, и debounce-timer)
		abortRef.current?.abort()
		const ctrl = new AbortController()
		abortRef.current = ctrl

		const payload: PresentationEstimatePayload = {
			mode: inputs.mode,
			prompt: trimmed,
			file_url: inputs.mode === 'edit' ? inputs.fileUrl : undefined,
			slide_count: inputs.mode === 'create' ? inputs.slideCount : undefined,
			image_count: inputs.imageCount,
		}

		const timer = setTimeout(() => {
			setEstimating(true)
			estimatePresentation(payload, ctrl.signal)
				.then((res) => {
					if (ctrl.signal.aborted) return
					setCreditsPrice(res.credits_price)
					setSlidesCount(res.slides_count ?? null)
				})
				.catch(() => {
					// network / abort / invalid — тихо, цена останется предыдущей
				})
				.finally(() => {
					if (!ctrl.signal.aborted) setEstimating(false)
				})
		}, DEBOUNCE_MS)

		return () => {
			clearTimeout(timer)
			ctrl.abort()
		}
	}, [
		inputs.enabled,
		inputs.mode,
		inputs.prompt,
		inputs.fileUrl,
		inputs.slideCount,
		inputs.imageCount,
	])

	return { creditsPrice, slidesCount, estimating }
}
